/* eslint-disable @typescript-eslint/no-empty-function */
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'react-feather';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import { PriceDeviationsTable } from './PriceDeviationsTable';
import styles from './PriceDeviationsDrawer.module.css';
import { PriceDeviationsDrawerSharedProps } from './PriceDeviationsDrawerShared.types';

/**
 * Shared price deviations drawer used by:
 * - Menu products (FZStoreMenuDetails)
 * - Modifiers (FZStoreMenuModifiersDetails)
 * - Bundle add-ons (FZBundleAddOnsView, EditBundleAddOnDrawer)
 * - Bundle upcharges (FZBundleUpcharges)
 */
export const PriceDeviationsDrawerShared: FC<PriceDeviationsDrawerSharedProps> = ({
  isOpen,
  onClose,
  selectedGroupName,
  data,
  isLoading = false,
  cameFromEditDrawer,
  firstColumnHeader,
  formatZeroPriceAsNone = false,
  closeButtonTestId = 'x-icon',
  backButtonTestId = 'backToEdit',
}) => {
  const { t } = useTranslation();
  const tableData = data ?? [];

  return (
    <div>
      <SwipeableDrawer open={isOpen} onClose={onClose} onOpen={() => {}} anchor="bottom">
        <div className={styles.container}>
          <header>
            <div>
              <h1>{t('priceDeviations.priceDeviations')}</h1>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" data-testid={closeButtonTestId}>
              <X size={24} color="var(--black-900)" />
            </button>
          </header>
          <div className={styles.content}>
            {cameFromEditDrawer && (
              <Button
                variant={ButtonVariants.Text}
                className={styles.backLink}
                onClick={onClose}
                data-testid={backButtonTestId}
              >
                <ArrowBackIcon fontSize="large" />
                <div>{t('priceDeviations.backToEdit')}</div>
              </Button>
            )}
            <div className={styles.selectedGroupName}>{selectedGroupName}</div>
            {isLoading ? (
              <div>{t('common.loading')}</div>
            ) : (
              <PriceDeviationsTable
                data={tableData}
                firstColumnHeader={firstColumnHeader}
                formatZeroPriceAsNone={formatZeroPriceAsNone}
              />
            )}
          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
};
